import type { DashboardOverview } from '../components/dashboard/DashboardStatsGrid';

export interface PartnerBreakdown {
  partnerDrivers: number;
  partnerDeliveryPartners: number;
  partnerMerchants: number;
  partnerVendors: number;
  partnerServiceProviders: number;
  totalPartners: number;
}

export function getPartnerBreakdown(overview: DashboardOverview = {}): PartnerBreakdown {
  const partnerDrivers = overview.partnerDrivers ?? 0;
  const partnerDeliveryPartners = overview.partnerDeliveryPartners ?? 0;
  const partnerMerchants = overview.partnerMerchants ?? 0;
  const partnerVendors = overview.partnerVendors ?? 0;
  const partnerServiceProviders = overview.partnerServiceProviders ?? 0;
  const computedTotal = partnerDrivers
    + partnerDeliveryPartners
    + partnerMerchants
    + partnerVendors
    + partnerServiceProviders;

  /** Headline total always matches the role breakdown shown in the hint. */
  const totalPartners = computedTotal > 0
    ? computedTotal
    : (overview.totalPartners ?? 0);

  return {
    partnerDrivers,
    partnerDeliveryPartners,
    partnerMerchants,
    partnerVendors,
    partnerServiceProviders,
    totalPartners,
  };
}

/** Summarise active partner accounts by role for dashboard hints. */
export function formatPartnerHint(overview: DashboardOverview = {}): string {
  const breakdown = getPartnerBreakdown(overview);
  const {
    partnerDrivers,
    partnerDeliveryPartners,
    partnerMerchants,
    partnerVendors,
    partnerServiceProviders,
    totalPartners,
  } = breakdown;

  if (totalPartners <= 0) return 'No active partner accounts';

  const roleParts = [
    partnerDrivers > 0 ? `${partnerDrivers.toLocaleString()} driver partners` : null,
    partnerDeliveryPartners > 0 ? `${partnerDeliveryPartners.toLocaleString()} delivery partners` : null,
    partnerMerchants > 0 ? `${partnerMerchants.toLocaleString()} merchants` : null,
    partnerVendors > 0 ? `${partnerVendors.toLocaleString()} vendors` : null,
    partnerServiceProviders > 0 ? `${partnerServiceProviders.toLocaleString()} service providers` : null,
  ].filter(Boolean);

  const totalLabel = `${totalPartners.toLocaleString()} active partner accounts (all roles)`;
  let hint = roleParts.length
    ? `${roleParts.join(' · ')} — ${totalLabel}`
    : totalLabel;

  const fleetProfiles = overview.totalDrivers ?? 0;
  if (fleetProfiles > 0 && fleetProfiles !== partnerDrivers) {
    hint += `. ${fleetProfiles.toLocaleString()} transport fleet profiles on file (vehicle records; not partner accounts)`;
  }

  return hint;
}
